import { useState, useEffect, useRef, useCallback } from 'react';
import { imageApi } from '../services/imageApi';

/**
 * Hook that fetches an image via authenticated request and returns a blob URL.
 * Supports lazy loading via IntersectionObserver – the image is only fetched
 * once its container scrolls into view (with a 200px rootMargin for preloading).
 *
 * Usage:
 *   const { blobUrl, loading, error, containerRef } = useAuthenticatedImage(url);
 *   return <div ref={containerRef}><img src={blobUrl} /></div>;
 */
export function useAuthenticatedImage(imageUrl: string | undefined) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageUrlRef = useRef(imageUrl);
  imageUrlRef.current = imageUrl;

  // Observe visibility
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !imageUrl) return;

    // If IntersectionObserver isn't available, just load immediately
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [imageUrl]);

  // Fetch once visible
  useEffect(() => {
    if (!imageUrl || !isVisible) return;

    let revoked = false;
    let objectUrl: string | null = null;

    setLoading(true);
    setError(false);

    imageApi
      .fetchImageBlob(imageUrl)
      .then((blob) => {
        if (!revoked) {
          objectUrl = URL.createObjectURL(blob);
          setBlobUrl(objectUrl);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!revoked) {
          setError(true);
          setLoading(false);
        }
      });

    return () => {
      revoked = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      if (imageUrl) {
        imageApi.releaseCachedBlob(imageUrl);
      }
    };
  }, [imageUrl, isVisible]);

  return { blobUrl, loading, error, containerRef };
}
